import { ActionIcon, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconTrash } from '@tabler/icons-react';
import { useState } from 'react';

import { api } from '../api';
import { apiErrorMessage } from '../utils/api-error';

type FolderActionsProps = {
  folderId: string;
  name: string;
  onDeleted: () => void | Promise<void>;
};

export function FolderActions({ folderId, name, onDeleted }: FolderActionsProps) {
  const [deleting, setDeleting] = useState(false);

  function confirmDelete() {
    modals.openConfirmModal({
      title: 'Delete folder?',
      children: `“${name}” will be permanently deleted. Only empty folders can be deleted.`,
      labels: { confirm: 'Delete folder', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setDeleting(true);
        try {
          const response = await api.api.api.v1.folders({ id: folderId }).delete();
          if (response.error) {
            notifications.show({
              color: 'red',
              title: `Delete failed: ${name}`,
              message: apiErrorMessage(response.error, {
                fallback: `“${name}” could not be deleted.`,
                status: response.status,
              }),
            });
            return;
          }
          await onDeleted();
          notifications.show({ color: 'green', message: `${name} deleted.` });
        } catch (error) {
          notifications.show({
            color: 'red',
            title: `Delete failed: ${name}`,
            message: apiErrorMessage(error, { fallback: `“${name}” could not be deleted.` }),
          });
        } finally {
          setDeleting(false);
        }
      },
    });
  }

  return (
    <Tooltip label='Delete folder'>
      <ActionIcon
        variant='subtle'
        color='red'
        aria-label={`Delete ${name}`}
        loading={deleting}
        onClick={(event) => {
          event.stopPropagation();
          confirmDelete();
        }}
      >
        <IconTrash size={18} />
      </ActionIcon>
    </Tooltip>
  );
}
